/**
 * components/debug/ConstraintDebug.tsx — Visual constraint helpers (T14).
 *
 * MUTE DEBUG component: Get the ALREADY resolved `ResolvedScene` and show
 * geometric helpers for visual verification of constraints (AC-1):
 *
 * - CONNECTOR PLANE: translucent plane parallel to Y-Z at y = h (normal +X),
 * with its normal (arrowHelper) at the origin [0, h, 0] (SPEC G1).
 * - ANCHOR POINTS: one ball for each `FastenerAnchor` (the fasteners),
 * colored by holeType (standard / inclined).
 * - alphaDeg AXIS: if `scene.alphaDeg !== 0`, an arrow along Z to
 * top of joist beam (SPEC G6, line parallel to Z at top).
 * - SISTEMI DI RIFERIMENTO (25-09-2026, richiesta utente — indagine incastro):
 * un tripode RGB (`THREE.AxesHelper`, X=rosso Y=verde Z=blu) per OGNI frame
 * gia' risolto — piano globale (`scene.plane`, piu' grande), ogni connettore
 * joist/header (`scene.connectors[i].position/quaternion`), ogni trave
 * joist/header (`scene.beams.*.center/quaternion`) — per verificare A OCCHIO
 * che le trasformazioni (segno Ry∓90°, mirror G3, flush) producano
 * l'orientamento/posizione attesi, invece di dedurlo da screenshot prospettici.
 *
 * Render ONLY — no position calculations (SPEC §6, AC-7). Colori standard
 * three.js per gli assi (rosso/verde/blu, non da config/colors.ts — sono una
 * convenzione universale X/Y/Z, non un colore di dominio del progetto).*/

import { Html } from "@react-three/drei";
import { useMemo } from "react";
import * as THREE from "three";
import type { ResolvedScene } from "../../model/types";
import { fastenerColorFor } from "../../model/fasteners";
import { COLORS } from "../../config/colors";
import { CONSTRAINT_DEBUG_DEFAULTS } from "../../config/defaults";
import styles from "./ConstraintDebug.module.css";

/** Colori standard three.js per gli assi (stessa convenzione di `THREE.AxesHelper`).*/
const AXIS_COLORS = { x: "#ff0000", y: "#00ff00", z: "#0000ff" } as const;

// ─── Props (PLAN §2.18) ───────────────────────────────────────

export interface ConstraintDebugProps {
  /** Fixed scene (floor, fasteners, beams, alphaDeg).*/
  scene: ResolvedScene;
}

//─── Component ─────────────────────── ────────────────────────

/**
 * Render constraint debug helpers from resolved scene.*/
export function ConstraintDebug({ scene }: ConstraintDebugProps) {
  const { plane } = scene;

  //Quaternion that orients a plane (local Z) along the normal of the plane
  //connector (render layer).
  const planeQuaternion = useMemo(
    () =>
      new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 0, 1),
        new THREE.Vector3(plane.normal[0], plane.normal[1], plane.normal[2]).normalize(),
      ),
    [plane.normal],
  );

  //alphaDeg axis (SPEC G6): Z-parallel line at the top of the joist beam.
  //With G5 (flush) the top of the joist beam coincides with the connector plane
  //(y = h): the AXIS OF ROTATION is the pivot of the hierarchy (fixed position
  //a y = plane.height), NOT the beam already rotated by alphaDeg.
  const alphaAxisVisible = scene.alphaDeg !== 0;
  const alphaAxisOrigin = [0, scene.plane.height, 0] as const;

  return (
    <group>
      {/*── Connector plane (parallel to Y-Z at y=h) ──*/}
      <mesh position={[plane.origin[0], plane.origin[1], plane.origin[2]]} quaternion={planeQuaternion}>
        <planeGeometry args={[CONSTRAINT_DEBUG_DEFAULTS.connectorPlane.width, CONSTRAINT_DEBUG_DEFAULTS.connectorPlane.height]} />
        <meshBasicMaterial
          color={COLORS.connectorPlane}
          side={THREE.DoubleSide}
          transparent
          opacity={CONSTRAINT_DEBUG_DEFAULTS.connectorPlane.opacity}
          depthWrite={false}
        />
      </mesh>

      {/*── Normal of the connector plane ──*/}
      <arrowHelper
        args={[
          new THREE.Vector3(plane.normal[0], plane.normal[1], plane.normal[2]),
          new THREE.Vector3(plane.origin[0], plane.origin[1], plane.origin[2]),
          CONSTRAINT_DEBUG_DEFAULTS.connectorNormal.length,
          COLORS.connectorPlane,
          CONSTRAINT_DEBUG_DEFAULTS.connectorNormal.headLength,
          CONSTRAINT_DEBUG_DEFAULTS.connectorNormal.headWidth,
        ]}
      />

      {/*── Fastener anchoring points ──*/}
      {scene.fasteners.map((anchor) => (
        <mesh key={anchor.id} position={[anchor.position[0], anchor.position[1], anchor.position[2]]}>
          <sphereGeometry args={[CONSTRAINT_DEBUG_DEFAULTS.fastenerPointRadius, 12, 12]} />
          <meshBasicMaterial color={fastenerColorFor(scene.fastenerLegend, anchor.fastenerItemId)} />
        </mesh>
      ))}

      {/*── alphaDeg axis (line parallel to Z at top of joist beam) ──*/}
      {alphaAxisVisible ? (
        <arrowHelper
          args={[
            new THREE.Vector3(0, 0, 1),
            new THREE.Vector3(alphaAxisOrigin[0], alphaAxisOrigin[1], alphaAxisOrigin[2]),
            CONSTRAINT_DEBUG_DEFAULTS.alphaAxis.length,
            COLORS.notchDebug,
            CONSTRAINT_DEBUG_DEFAULTS.alphaAxis.headLength,
            CONSTRAINT_DEBUG_DEFAULTS.alphaAxis.headWidth,
          ]}
        />
      ) : null}

      {/*── Sistemi di riferimento (tripodi RGB + etichette) ──────────────
      Piano globale (radice, X=0 SPEC G1) + un tripode per ogni connettore e
      ogni trave gia' risolti (position/quaternion WORLD, nessun calcolo qui).*/}
      <FrameAxes
        label="piano"
        position={plane.origin}
        quaternion={IDENTITY_QUAT}
        size={CONSTRAINT_DEBUG_DEFAULTS.frameAxes.plane}
      />
      {scene.connectors.map((instance) => (
        <FrameAxes
          key={`axes-${instance.side}-${instance.index}`}
          label={`${instance.side}[${instance.index}]`}
          position={instance.position}
          quaternion={instance.quaternion}
          size={CONSTRAINT_DEBUG_DEFAULTS.frameAxes.frame}
        />
      ))}
      <FrameAxes
        label="trave joist"
        position={scene.beams.joist.center}
        quaternion={scene.beams.joist.quaternion}
        size={CONSTRAINT_DEBUG_DEFAULTS.frameAxes.frame}
      />
      <FrameAxes
        label="trave header"
        position={scene.beams.header.center}
        quaternion={scene.beams.header.quaternion}
        size={CONSTRAINT_DEBUG_DEFAULTS.frameAxes.frame}
      />
    </group>
  );
}

//─── Internal sub-component ─────────────────────────────────

const IDENTITY_QUAT = new THREE.Quaternion();

/**
 * FrameAxes — tripode RGB (`THREE.AxesHelper`: X=rosso, Y=verde, Z=blu) su un
 * sistema di riferimento gia' risolto (position/quaternion WORLD), con
 * etichetta del nome del frame all'origine e etichette X/Y/Z in punta a
 * ciascun asse (25-09-2026, richiesta utente). Nessun calcolo qui: renderizza
 * esattamente il frame che riceve — le etichette X/Y/Z sono figlie dello
 * STESSO gruppo (posizione LOCALE `[size,0,0]` ecc.), quindi ereditano
 * automaticamente position+quaternion del frame senza calcoli aggiuntivi.*/
function FrameAxes({
  label,
  position,
  quaternion,
  size,
}: {
  label: string;
  position: readonly [number, number, number];
  quaternion: THREE.Quaternion;
  size: number;
}) {
  const axes = useMemo(() => new THREE.AxesHelper(size), [size]);
  return (
    <group position={[position[0], position[1], position[2]]} quaternion={quaternion}>
      <primitive object={axes} />
      <Html position={[0, 0, 0]} center sprite occlude={false}>
        <span className={styles.frameLabel}>{label}</span>
      </Html>
      <Html position={[size, 0, 0]} center sprite occlude={false}>
        <span className={styles.axisLabel} style={{ color: AXIS_COLORS.x }}>
          X
        </span>
      </Html>
      <Html position={[0, size, 0]} center sprite occlude={false}>
        <span className={styles.axisLabel} style={{ color: AXIS_COLORS.y }}>
          Y
        </span>
      </Html>
      <Html position={[0, 0, size]} center sprite occlude={false}>
        <span className={styles.axisLabel} style={{ color: AXIS_COLORS.z }}>
          Z
        </span>
      </Html>
    </group>
  );
}
